import { InfoCircle } from '@/components/icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FREE_INSTRUMENT_REFERENCE_MAX_LENGTH } from '@/constants';
import type { ChangeEvent, ReactNode } from 'react';

/** What the Satepsi catalog would have supplied, minus the table itself. */
export type InstrumentIdentity = {
    name: string;
    reference: string;
};

interface InstrumentIdentityFieldsProps {
    values: InstrumentIdentity;
    onChange: (values: InstrumentIdentity) => void;
    /** Prefixes the two `id`s, so a dialog can hold these fields more than once. */
    idPrefix: string;
    /** Marks the name with the required asterisk. The reference is never required. */
    nameRequired?: boolean;
    /**
     * Claims the focus on mount. For a dialog that *replaced* another one in
     * the shared modal slot: Radix only runs its open-autofocus when the dialog
     * opens, and a swap never reopens it, so the new content has to take the
     * focus itself.
     *
     * Not for `PasteInstrumentModal`, whose paste listener stands down while a
     * text field holds the focus — see the comment at its call site.
     */
    autoFocusName?: boolean;
    /**
     * Rendered directly under the name input, inside its own field group.
     *
     * The one thing that ever goes here is the pasted table's suggestion, and
     * it goes here rather than above the whole form because a proposal belongs
     * next to the field it writes into.
     */
    nameFooter?: ReactNode;
}

/**
 * The name and the citation of an instrument that is not in the catalog.
 *
 * Shared by the two ways of creating one — the free-instrument form and the
 * clipboard paste — because the pasted table needs exactly what the form asks
 * for, and two copies of these labels, this tooltip and this counter would
 * drift the moment either is reworded.
 */
export function InstrumentIdentityFields({
    values,
    onChange,
    idPrefix,
    nameRequired = false,
    autoFocusName = false,
    nameFooter,
}: InstrumentIdentityFieldsProps) {
    const nameId = `${idPrefix}-name`;
    const referenceId = `${idPrefix}-reference`;

    return (
        <>
            <div className="space-y-2">
                <Label htmlFor={nameId}>
                    Nome do instrumento{' '}
                    {nameRequired && <span className="text-destructive">*</span>}
                </Label>
                <Input
                    id={nameId}
                    autoFocus={autoFocusName}
                    value={values.name}
                    placeholder="Ex: Escala de Rastreio Institucional"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onChange({ ...values, name: event.target.value })
                    }
                />
                {nameFooter}
            </div>

            <div className="space-y-2">
                {/* Same shape as `chart-config/FieldLabel`: the helper prose
                    that used to sit under the field rides a hover tooltip
                    beside the label instead. The trigger is a sibling of the
                    Label, never a child — nesting it would fold "Mais
                    informações" into the label's accessible name and make
                    clicking the hint focus the textarea. */}
                <div className="flex items-center gap-1.5">
                    <Label htmlFor={referenceId}>Referência</Label>
                    {/* No global TooltipProvider exists: these fields render
                        from ModalProvider, outside the sidebar's. */}
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    aria-label="Mais informações"
                                    className="flex size-4 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                                >
                                    <InfoCircle className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs whitespace-normal">
                                Vira a nota de rodapé do instrumento, exatamente como você escrever
                                (ABNT, APA ou o formato da sua instituição).
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <Textarea
                    id={referenceId}
                    value={values.reference}
                    rows={3}
                    maxLength={FREE_INSTRUMENT_REFERENCE_MAX_LENGTH}
                    placeholder="Ex: SOBRENOME, N. Nome do instrumento. Cidade: Editora, 2019."
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                        onChange({ ...values, reference: event.target.value })
                    }
                    className="resize-none"
                />
                <p className="text-right text-xs text-muted-foreground">
                    {values.reference.length}/{FREE_INSTRUMENT_REFERENCE_MAX_LENGTH}
                </p>
            </div>
        </>
    );
}
